import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router";
import { fetchProductById } from '../item.js';
import { addToCart } from '../Cart-Context/context';
import { useSession } from '../Cart-Context/session'; 
import { trackEvent } from '../utils/trackEvents'; 
import { getProductImage } from "../utils/imageHelper.js";
import "../ProductDetails.css";

const ProductDetails = () => {
    const { sessionId, experimentId } = useSession(); // Get the current sessionId and experimentId
    const navigate = useNavigate();
    const { id } = useParams(); 
    const location = useLocation();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [showNutrition, setShowNutrition] = useState(false);
    const [quantity, setQuantity] = useState(1);
    const [popup, setPopup] = useState("");

    // Helper to turn -1 or null into a clean dash
    const formatValue = (val, unit = "g") => {
        if (val === null || val === -1 || val === "-1") return "—"; 
        return `${val}${unit}`;
    };

    useEffect(() => {
        async function loadProduct() {
            try {
                const data = await fetchProductById(id);
                console.log("Fetched product:", data);
                setProduct(data);

                // Track the Page View once the product data is loaded
                trackEvent(sessionId, 'PAGE_VIEW', `Product Details: ${data.name}`, {}, experimentId);

            } catch (err) {
                console.error("Failed to load product:", err);
                setError("Failed to load product.");
            } finally {
                setLoading(false);
            }
        }
        
        loadProduct();
    }, [id, sessionId, experimentId]);
    
    const handleMinusQuantity = () => {
        const newQty = quantity - 1 < 1 ? 1 : quantity - 1;
        trackEvent(sessionId, 'CLICK', `Decrease Qty: ${product.name}`, { newQuantity: newQty }, experimentId);
        setQuantity(newQty);
    }
    const handlePlusQuantity = () => {
        const newQty = quantity + 1;
        trackEvent(sessionId, 'CLICK', `Increase Qty: ${product.name}`, { newQuantity: newQty }, experimentId);
        setQuantity(newQty);
    }

    async function handleAddToCart(quantity, productId) {
        try {
            const currentCartId = localStorage.getItem("cartId");
            if(!sessionId && !currentCartId) {
                setPopup("Session expired. Please refresh.");
                setTimeout(() => { setPopup("")}, 3000);
                return;
            }

            const success = await addToCart(quantity, productId);
            trackEvent(sessionId, 'CLICK', `Add to Cart: ${product.name}`, { 
                quantity: quantity, 
                price: product.price,
                success: !!success 
            }, experimentId);

            if(success) {
                setPopup("Added To Cart!");
                window.dispatchEvent(new Event("cartUpdated"));
                setTimeout(() => { setPopup("")}, 3000);
            } else {
                setPopup("Failed to add to cart.");
                setTimeout(() => { setPopup("")}, 3000);
            }
        } catch (error) {
            console.error("Cart error:", error);
            setPopup("Item Not Added To Cart");
            setTimeout(() => { setPopup("")}, 3000);
        }
    }

    const handleBack = () => {
        trackEvent(sessionId, 'CLICK', `Back from ${product.name}`, {}, experimentId);
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate("/products"); 
        }
    };

    const toggleNutrition = () => {
        const newState = !showNutrition;
        if (newState) {
            trackEvent(sessionId, 'CLICK', `Nutrition Facts Open: ${product.name}`, {}, experimentId);
        }
        setShowNutrition(newState);
    };

    if (loading) return <p>Loading product...</p>;
    if (error) return <p>{error}</p>;
    if (!product) return <p>Product not found.</p>;
    
    return (
    <main className="page-container">
        <div className="product-detail-container">
            
            <div className="product-detail-image-section">
    <img 
        /* If the image path DOES NOT start with http, add the local-style class */
        className={`detail-image ${!(product.productImage || product.image)?.startsWith('http') ? 'local-image-style' : ''}`}
        src={getProductImage(product.productImage || product.image)} 
        alt={product.name} 
        onError={(e) => {
            if (e.target.src !== window.location.origin + "/autoFill.jpg") {
                e.target.src = "/autoFill.jpg";
            }
        }}
    />
</div>

            <div className="product-info-section">
                <h1 className="detail-title">{product.name}</h1>
                
                <h2 className="detail-price">
                    <p><strong>{product.price != null ? `$${Number(product.price.toString().replace('$', '')).toFixed(2)}` : "$0.00"}</strong></p>
                </h2>

                <div className="detail-actions">
                    <div className="detail-qty-pill">
                        <button className="qty-btn" onClick={handleMinusQuantity}>-</button>
                        <span className="qty-number">{quantity}</span>
                        <button className="qty-btn" onClick={handlePlusQuantity}>+</button>
                    </div>

                    <button 
                        className="detail-btn-main"
                        onClick={() => handleAddToCart(quantity, product.id || product.productId)}
                    >
                        Add To Cart
                    </button>
                </div>

                <button className="go-back-link" onClick={handleBack}>
                    ← Back to Products
                </button>
            </div>
        </div>

        <div
            className={`product-details-dropdown-container open`} 
            style={{ marginTop: "40px", borderTop: "1px solid #eee" }}
        >
            <div className="nutrition-grid">
                <h1><strong>Nutrition Facts</strong></h1>
                <p><strong>Ingredients:</strong> {product.nutrition?.ingredients || "—"}</p>
                <p><strong>Warnings:</strong> {product.nutrition?.warnings === "-1" ? "—" : product.nutrition?.warnings}</p>
                <p><strong>Calories:</strong> {formatValue(product.nutrition?.calories, " Cal")}</p>
                <p><strong>Total Fat:</strong> {formatValue(product.nutrition?.totalFat)}</p>
                <p><strong>Monounsaturated Fat:</strong> {formatValue(product.nutrition?.monounsaturatedFat)}</p>
                <p><strong>Polyunsaturated Fat:</strong> {formatValue(product.nutrition?.polyunsaturatedFat)}</p>
                <p><strong>Trans Fat:</strong> {formatValue(product.nutrition?.transFat)}</p>
                <p><strong>Protein:</strong> {formatValue(product.nutrition?.protein)}</p>
                <p><strong>Total Carbs:</strong> {formatValue(product.nutrition?.totalCarbs)}</p>
                <p><strong>Total Sugars:</strong> {formatValue(product.nutrition?.totalSugars)}</p>
                <p><strong>Added Sugar:</strong> {formatValue(product.nutrition?.addedSugar)}</p>
                <p><strong>Cholesterol:</strong> {formatValue(product.nutrition?.cholesterol, "mg")}</p>
                <p><strong>Sodium:</strong> {formatValue(product.nutrition?.sodium, "mg")}</p>
               <p><strong>Fiber:</strong> {formatValue(product.nutrition?.fiber)}</p>
                <p><strong>Iron:</strong> {formatValue(product.nutrition?.iron, "mg")}</p>
                <p><strong>Serving Size:</strong> {product.nutrition?.servingSize || "—"}</p>
                <p><strong>Servings Per Container:</strong> {product.nutrition?.servingsPerContainer || "—"}</p>
            </div>
        </div>

        {popup && (
            <div className="popup">
                <h2>{popup}</h2>
            </div>
        )}
    </main>
);
}
export default ProductDetails;